import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

import { ArrowLeft, ArrowRight, BadgeCheck, Check, CheckCircle2, ChevronDown, ClipboardList, CreditCard, ImagePlus, Loader2, MapPin, Minus, Package, Plus, RefreshCw, Search, Store, UploadCloud, X } from 'lucide-react';

import axiosClient from '../../api/axiosClient';

import '../../styles/Marketing/field-sales.css';



const STEPS = [

  { title: 'Establishment', subtitle: "Business information", icon: Store },

  { title: "Branding", subtitle: "Logo & online presence", icon: ImagePlus },

  { title: 'Subscription', subtitle: "Choose a plan", icon: CreditCard },

  { title: 'QR products', subtitle: "Items & review", icon: Package },

];

const INITIAL = { business_name: '', business_type: 'restaurant', contact_name: '', phone: '', city: '', website_url: '', subscription_period: 'none', custom_subscription_price: '', custom_subscription_length: '', custom_subscription_unit: 'months', notes: '' };

const formatMoney = (value) => `${Number(value || 0).toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DH`;

const getPrice = (product) => {

  const raw = product.base_unit_price ?? product.price ?? product.base_price ?? product.unit_price;

  return raw === null || raw === undefined || raw === '' || !Number.isFinite(Number(raw)) ? null : Number(raw);

};

const getProducts = (response) => {

  const body = response?.data;

  // Laravel wraps the paginator inside `data`: { data: { current_page, data: [...] } }.

  const candidates = [body?.data?.data, body?.data, body?.products?.data, body?.products, body?.items?.data, body?.items, body?.catalog?.data, body?.catalog, body];

  return candidates.find(Array.isArray) ?? null;

};

const optionLabel = (items, value) => (items || []).find((item) => item.value === value)?.label || value || "Not specified";

const getError = (error) => {

  const validation = error?.response?.data?.errors;

  const first = validation && Object.values(validation).flat()[0];

  return first || error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';

};





const BUSINESS_TYPES = [

  { value: 'restaurant', label: "Restaurant" },

  { value: 'cafe', label: "Café" },

  { value: 'other', label: "Other" },

];

function FieldDropdown({ value, options, onChange, label, placeholder = "Select an option" }) {
  const { t } = useLanguage();
  const tr = (key, fallback) => {
    const value = t(`fieldSales.${key}`);
    return value && value !== `fieldSales.${key}` ? value : fallback;
  };


  const [open, setOpen] = useState(false);

  const containerRef = useRef(null);

  const buttonRef = useRef(null);

  const optionRefs = useRef([]);

  const selectedIndex = options.findIndex((option) => option.value === value);

  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

  useEffect(() => {

    if (!open) return undefined;

    const onOutside = (event) => {

      if (!containerRef.current?.contains(event.target)) setOpen(false);

    };

    const onEscape = (event) => {

      if (event.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); }

    };

    document.addEventListener('pointerdown', onOutside);

    document.addEventListener('keydown', onEscape);

    return () => { document.removeEventListener('pointerdown', onOutside); document.removeEventListener('keydown', onEscape); };

  }, [open]);

  const selectOption = (option) => { onChange(option.value); setOpen(false); buttonRef.current?.focus(); };

  const onTriggerKeyDown = (event) => {

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {

      event.preventDefault(); setOpen(true);

      requestAnimationFrame(() => optionRefs.current[Math.max(0, selectedIndex)]?.focus());

    }

  };

  const onOptionKeyDown = (event, index) => {

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {

      event.preventDefault();

      const next = (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;

      optionRefs.current[next]?.focus();

    } else if (event.key === 'Home' || event.key === 'End') {

      event.preventDefault(); optionRefs.current[event.key === 'Home' ? 0 : options.length - 1]?.focus();

    } else if (event.key === 'Tab') setOpen(false);

  };

  return (

    <div className={`fs-dropdown ${open ? 'is-open' : ''}`} ref={containerRef}>

      <button ref={buttonRef} type="button" className="fs-dropdown-trigger" aria-label={label} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((previous) => !previous)} onKeyDown={onTriggerKeyDown}>

        <span className="fs-dropdown-value">{selectedOption?.hex && <span className="fs-color-dot" style={{ background: selectedOption.hex }} aria-hidden="true" />}<span>{selectedOption?.label || tr('s59d5d61e9e', "Select an option")}</span></span><ChevronDown size={17} aria-hidden="true" />

      </button>

      {open && <div className="fs-dropdown-menu" role="listbox" aria-label={label}>

        {options.map((option, index) => <button key={option.value} ref={(node) => { optionRefs.current[index] = node; }} type="button" role="option" aria-selected={value === option.value} className={`fs-dropdown-option ${value === option.value ? 'is-selected' : ''}`} onClick={() => selectOption(option)} onKeyDown={(event) => onOptionKeyDown(event, index)}>

          <span className="fs-dropdown-value">{option.hex && <span className="fs-color-dot" style={{ background: option.hex }} aria-hidden="true" />}<span>{option.label}</span></span>{value === option.value && <Check size={16} aria-hidden="true" />}

        </button>)}

      </div>}

    </div>

  );

}



/** Loads image bytes using the same authenticated Axios client as catalog API calls. */
function FieldSalesProductImage({ productId, imagePath, type = 'main', index = 0, alt = '' }) {
  const { t } = useLanguage();
  const tr = (key, fallback) => {
    const value = t(`fieldSales.${key}`);
    return value && value !== `fieldSales.${key}` ? value : fallback;
  };

  const [preview, setPreview] = useState({ url: null, status: 'loading' });

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    setPreview({ url: null, status: imagePath ? 'loading' : 'missing' });
    if (!productId || !imagePath) return () => { active = false; };

    axiosClient.get(`/qr-print/products/${encodeURIComponent(productId)}/images/${type}/${index}`, {
      responseType: 'blob',
    }).then(({ data }) => {
      if (!active) return;
      if (!(data instanceof Blob) || !data.size || (data.type && !data.type.startsWith('image/'))) {
        throw new Error('Image endpoint did not return an image');
      }
      objectUrl = URL.createObjectURL(data);
      setPreview({ url: objectUrl, status: 'ready' });
    }).catch((error) => {
      if (active) {
        console.warn('QR product image unavailable', productId, type, index, error?.response?.status || error?.message);
        setPreview({ url: null, status: 'error' });
      }
    });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [productId, imagePath, type, index]);

  if (!preview.url) {
    return <span title={preview.status === 'error' ? tr('s8e9bc4ccf5', "Image could not be loaded") : undefined}>
      <Package size={23} aria-label={preview.status === 'loading' ? tr('s3969df55b0', "Loading image") : tr('sc4475f85d5', "Image unavailable")}/>
    </span>;
  }
  return <img src={preview.url} alt={alt} onError={() => setPreview({ url: null, status: 'error' })}/>;
}

export default function FieldSales() {
  const { t } = useLanguage();
  const tr = useCallback((key, fallback) => {
    const translated = t(`fieldSales.${key}`);
    return translated && translated !== `fieldSales.${key}` ? translated : fallback;
  }, [t]);

  const [step, setStep] = useState(0);
  const localizedSteps = STEPS.map((entry, index) => ({ ...entry,
    title: tr(['establishment','branding','subscription','qrProducts'][index], entry.title),
    subtitle: tr(['businessInfo','logoPresence','choosePlan','itemsReview'][index], entry.subtitle),
  }));
  const businessTypes = BUSINESS_TYPES.map((entry) => ({ ...entry,
    label: tr(`businessType_${entry.value}`, entry.label),
  }));

  const [form, setForm] = useState({ ...INITIAL });

  const [logo, setLogo] = useState(null);

  const [logoPreview, setLogoPreview] = useState('');

  const [catalog, setCatalog] = useState([]);

  const [quantities, setQuantities] = useState({});

  const [productPreferences, setProductPreferences] = useState({});

  const [activeProduct, setActiveProduct] = useState(null);

  const [catalogLoading, setCatalogLoading] = useState(true);

  const [catalogError, setCatalogError] = useState('');

  const [productSearch, setProductSearch] = useState('');

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState('');

  const [success, setSuccess] = useState(null);

  const setField = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));



  const loadProducts = useCallback(async () => {

    setCatalogLoading(true);

    setCatalogError('');

    try {

      const response = await axiosClient.get('/qr-print/products', { params: { per_page: 100 } });

      const products = getProducts(response);

      if (!products) throw new Error('The QR catalog API returned an unexpected response format. Check GET /qr-print/products in Network.');

      setCatalog(products);

    } catch (err) {

      setCatalog([]);

      const status = err?.response?.status;

      const details = getError(err);

      setCatalogError(status === 403

        ? `Access denied (403): ${details}. The Laravel QR Print catalog must allow salesrepresentative users READ-only access.`

        : status === 404

          ? `Catalog endpoint not found (404). Check your axiosClient base URL and Laravel QR Print routes. ${details}`

          : status === 401

            ? `Authentication required (401). Check the sales account login token. ${details}`

            : status

              ? `QR catalog request failed (${status}): ${details}`

              : details);

    } finally {

      setCatalogLoading(false);

    }

  }, []);



  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {

    if (!logo) { setLogoPreview(''); return undefined; }

    const url = URL.createObjectURL(logo);

    setLogoPreview(url);

    return () => URL.revokeObjectURL(url);

  }, [logo]);



  const selectedProducts = useMemo(() => Object.entries(quantities)
    .filter(([, qty]) => Number(qty) > 0)
    .map(([product_id, quantity]) => ({ product_id: Number(product_id), quantity: Number(quantity) })), [quantities]);

  const productUnits = selectedProducts.reduce((total, item) => total + item.quantity, 0);

  // Calculate displayed amounts from catalog prices. Never infer a missing price as zero.
  const pricing = useMemo(() => {
    let subtotal = 0;
    let complete = true;
    const pricedUnits = [];
    selectedProducts.forEach(({ product_id, quantity }) => {
      const product = catalog.find((entry) => Number(entry.id) === product_id);
      const price = product ? getPrice(product) : null;
      if (price === null) {
        complete = false;
      } else {
        subtotal += price * quantity;
        pricedUnits.push({ price, quantity });
      }
    });
    const freeUnits = Math.floor(productUnits / 10);
    let remainingFree = freeUnits;
    let discount = 0;
    pricedUnits.sort((a, b) => a.price - b.price);
    if (complete) {
      pricedUnits.forEach(({ price, quantity }) => {
        const freeHere = Math.min(quantity, remainingFree);
        discount += freeHere * price;
        remainingFree -= freeHere;
      });
    }
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      complete,
      freeUnits,
      discount: complete ? Math.round(discount * 100) / 100 : null,
    };
  }, [catalog, selectedProducts, productUnits]);

  const productTotal = pricing.subtotal;
  const pricesComplete = pricing.complete;
  const discountedProductTotal = pricesComplete ? Math.max(0, productTotal - pricing.discount) : null;

  const subscriptionPrice = form.subscription_period === 'none' ? 0
    : form.subscription_period === 'yearly' ? 500
    : form.subscription_period === 'custom' ? Number(form.custom_subscription_price || 0)
    : 50;

  const subscriptionLabel = form.subscription_period === 'none' ? tr('subscriptionNone', 'No subscription')
    : form.subscription_period === 'yearly' ? tr('s9e40e458f6', "Annual subscription (12 months)")
    : form.subscription_period === 'custom' ? `${tr('subscriptionCustom', 'Custom subscription')} (${form.custom_subscription_length || '—'} ${tr('unit_'+form.custom_subscription_unit, form.custom_subscription_unit)})`
    : tr('s76c80b19f6', "Monthly subscription (1 month)");

  const validateSubscription = () => {
    if (form.subscription_period !== 'custom') return '';
    if (form.custom_subscription_price === '' || !Number.isFinite(Number(form.custom_subscription_price)) || Number(form.custom_subscription_price) < 0) {
      return tr('s0131a29450', "Enter a valid custom subscription price (0 or more).");
    }
    if (!Number.isInteger(Number(form.custom_subscription_length)) || Number(form.custom_subscription_length) < 1) {
      return tr('s3c87768933', "Enter a custom subscription duration of at least 1.");
    }
    return '';
  };

  const visibleProducts = useMemo(() => catalog.filter((product) =>

    String(product.name || product.title || '').toLowerCase().includes(productSearch.toLowerCase().trim())

  ), [catalog, productSearch]);



  const validateEstablishment = () => {

    if (!form.business_name.trim()) return tr('sb459e1be8a', "Enter the establishment name.");

    if (!form.phone.trim()) return tr('sa954ea12be', "Enter a contact phone number.");

    return '';

  };

  const validateBranding = () => {

    if (form.website_url.trim()) {

      try { if (!['http:', 'https:'].includes(new URL(form.website_url.trim()).protocol)) throw new Error('Invalid protocol'); }

      catch { return tr('sb15fb8144d', "Enter a valid link starting with https\\:// or http\\://."); }

    }

    if (logo && (!['image/jpeg', 'image/png', 'image/webp'].includes(logo.type) || logo.size > 2 * 1024 * 1024)) {

      return tr('sdd83900d85', "The logo must be JPG, PNG or WebP and no larger than 2 MB.");

    }

    return '';

  };

  const goToStep = (target) => {

    const issue = target > step ? (validateEstablishment() || (target > 1 ? validateBranding() : '') || (target > 2 ? validateSubscription() : '')) : '';

    if (issue) { setError(issue); setStep(!validateEstablishment() ? 1 : 0); return; }

    setError('');

    setStep(Math.min(3, Math.max(0, target)));

  };

  const openProduct = (product) => {

    setProductPreferences((old) => ({

      ...old,

      [product.id]: old[product.id] || {

        color: product.available_colors?.[0]?.value || '',

        material: product.available_materials?.[0]?.value || '',

        size: product.available_sizes?.[0]?.value || '',

        design: '', design_notes: ''

      }

    }));

    setActiveProduct(product);

  };

  const setPreference = (id, field, value) => setProductPreferences((old) => ({ ...old, [id]: { ...old[id], [field]: value } }));

  useEffect(() => {

    if (!activeProduct) return undefined;

    const closeOnEscape = (event) => { if (event.key === 'Escape') setActiveProduct(null); };

    window.addEventListener('keydown', closeOnEscape);

    const original = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => { window.removeEventListener('keydown', closeOnEscape); document.body.style.overflow = original; };

  }, [activeProduct]);

  const updateQuantity = (id, value) => {

    const quantity = Math.min(10000, Math.max(0, Math.floor(Number(value) || 0)));

    setQuantities((previous) => ({ ...previous, [id]: quantity }));

  };

  // Only the dedicated button on step 4 is allowed to send the API request.

  const submitFinal = async () => {

    if (step !== 3) return;

    if (saving || success) return;

    const issue = validateEstablishment() || validateBranding() || validateSubscription();

    if (issue) { setError(issue); setStep(validateEstablishment() ? 0 : validateBranding() ? 1 : 2); return; }

    setSaving(true);

    setError('');

    try {

      const body = new FormData();

      // Laravel now validates custom subscription price, length and duration unit.
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'notes') return;
        if (key.startsWith('custom_subscription_') && form.subscription_period !== 'custom') return;
        if (value !== '') body.append(key, value);
      });

      // Include preferences in the existing notes field so they survive even when

      // the Laravel endpoint only accepts product_id and quantity per item.

      const designSummary = selectedProducts.map((item) => {

        const product = catalog.find((entry) => Number(entry.id) === item.product_id);

        const pref = productPreferences[item.product_id] || {};

        return `${product?.name || `Product #${item.product_id}`} (x${item.quantity}): size=${optionLabel(product?.available_sizes, pref.size)}, material=${optionLabel(product?.available_materials, pref.material)}, color=${optionLabel(product?.available_colors, pref.color)}, design=${pref.design || 'Not selected'}, design instructions=${pref.design_notes || 'None'}`;

      });

      const subscriptionSummary = form.subscription_period === 'custom'
        ? `CUSTOM SUBSCRIPTION REQUEST: ${formatMoney(subscriptionPrice)} total for ${form.custom_subscription_length} ${form.custom_subscription_unit}. Requires admin approval.`
        : form.subscription_period === 'none'
          ? 'NO SUBSCRIPTION REQUESTED: products only.'
          : `SUBSCRIPTION REQUEST: ${subscriptionLabel}; ${formatMoney(subscriptionPrice)}.`;
      const priceSummary = `QR PRODUCTS: subtotal ${pricesComplete ? formatMoney(productTotal) : tr('sa86c864c06', "Price on request")}; estimated promotion ${pricesComplete ? formatMoney(pricing.discount) : 'pending'}; discounted products ${pricesComplete ? formatMoney(discountedProductTotal) : 'pending'}.`;
      const mergedNotes = [form.notes.trim(), subscriptionSummary, priceSummary, designSummary.length ? `QR PRODUCT PREFERENCES:\n${designSummary.join('\n')}` : ''].filter(Boolean).join('\n\n');

      if (mergedNotes) body.append('notes', mergedNotes);

      if (logo) body.append('logo', logo);

      selectedProducts.forEach((item, index) => {

        body.append(`products[${index}][product_id]`, String(item.product_id));

        body.append(`products[${index}][quantity]`, String(item.quantity));

      });

      const response = await axiosClient.post('/marketing/field-visits', body);

      setSuccess(response.data?.registration || response.data?.data || {});

    } catch (err) { setError(getError(err)); }

    finally { setSaving(false); }

  };

  const reset = () => {

    setForm({ ...INITIAL }); setLogo(null); setQuantities({}); setProductPreferences({}); setActiveProduct(null); setProductSearch('');

    setStep(0); setSuccess(null); setError('');

  };



  if (success) return (

    <main className="fs-shell"><section className="fs-success fs-surface">

      <span className="fs-success-icon"><CheckCircle2 size={38} /></span>

      <span className="fs-eyebrow">{tr('s680d02a6cb', "VISIT SUBMITTED")}</span>

      <h1>{tr('see3fb37daa', "Registration received")}</h1>

      <p>{tr('sf4c65d9604', "The establishment and subscription request were submitted for review. Subscription activation and payment verification are still pending.")}</p>

      {success.id && <div className="fs-success-id">{tr('scbb8ae7725', "Registration reference")} <strong>#{success.id}</strong></div>}

      <div className="fs-actions fs-success-actions"><button type="button" className="fs-primary" onClick={reset}><Plus size={17} />{tr('s72cce7245a', "Register another")}</button><Link to="/marketing/visits" className="fs-secondary">{tr('sac05dd28ce', "View my visits")} <ArrowRight size={17} /></Link></div>

    </section></main>

  );



  return (

    <main className="fs-shell">

      <header className="fs-page-heading">

        <div><span className="fs-eyebrow"><MapPin size={13} />{tr('s22bbf57de2', "FIELD SALES WORKSPACE")}</span><h1>{tr('sec330d2032', "Register an establishment")}</h1><p>{tr('sf2f31adb54', "Capture a new visit, choose a subscription and prepare a QR print request.")}</p></div>

        <Link className="fs-secondary fs-view-visits" to="/marketing/visits"><ClipboardList size={17} />{tr('s1be2f5ca1c', "My visits")}</Link>

      </header>

      <div className="fs-workspace">

        <div className="fs-main-column">

          <nav className="fs-stepper fs-surface" aria-label={tr('sfaae2b7acf', "Registration steps")}>

            {localizedSteps.map(({ title, icon: Icon }, index) => (

              <button key={title} type="button" onClick={() => { if (index <= step) goToStep(index); }} className={`fs-step ${index === step ? 'is-current' : ''} ${index < step ? 'is-done' : ''}`} aria-current={index === step ? 'step' : undefined} disabled={index > step}>

                <span className="fs-step-number">{index < step ? <Check size={17} /> : <Icon size={18} />}</span><span className="fs-step-copy"><strong>{title}</strong><small>Step {index + 1} of 4</small></span>

              </button>

            ))}

          </nav>

          <form className="fs-form fs-surface" onSubmit={(event) => event.preventDefault()} noValidate>

            <div className="fs-form-header"><div><span className="fs-section-kicker">STEP {step + 1} / 4</span><h2>{localizedSteps[step].title}</h2><p>{localizedSteps[step].subtitle}</p></div><span className="fs-header-icon">{(() => { const Icon = localizedSteps[step].icon; return <Icon size={23} />; })()}</span></div>

            {step === 0 && <div className="fs-fields">

              <label className="fs-field fs-span-full"><span>{tr('s24d7a68cb6', "Establishment name")} <em>*</em></span><input value={form.business_name} onChange={(e) => setField('business_name', e.target.value)} placeholder={tr('sf315e9cc81', "e.g. Café Atlas")} autoComplete="organization" /></label>

              <div className="fs-field"><span>{tr('s56396cec5c', "Business type")}</span><FieldDropdown label={tr('s56396cec5c', "Business type")} value={form.business_type} options={businessTypes} onChange={(value) => setField('business_type', value)} /></div>

              <label className="fs-field"><span>{tr('sc450f4aa9a', "City / neighborhood")}</span><input value={form.city} onChange={(e) => setField('city', e.target.value)} placeholder={tr('sc7bd2d0dc8', "e.g. Casablanca")} /></label>

              <label className="fs-field"><span>{tr('s257548fcc3', "Contact person")}</span><input value={form.contact_name} onChange={(e) => setField('contact_name', e.target.value)} placeholder={tr('sb74f18c13c', "Owner or manager")} autoComplete="name" /></label>

              <label className="fs-field"><span>{tr('s1f8261d174', "Phone number")} <em>*</em></span><input type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="+212 6 XX XX XX XX" autoComplete="tel" /></label>

            </div>}

            {step === 1 && <div className="fs-branding">

              <div className="fs-upload-section"><span className="fs-input-label">{tr('sf14a75f43a', "Establishment logo")} <small>{tr('sf53d1cd25e', "(optional)")}</small></span>

                <label className="fs-upload"><UploadCloud size={29} /><strong>{logo ? tr('s69b0146611', "Change uploaded logo") : tr('sc3a790450c', "Choose a logo")}</strong><span>{tr('s7e7488d62d', "JPG, PNG or WebP • Maximum 2 MB")}</span><input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setLogo(e.target.files?.[0] || null)} /></label>

                {logoPreview && <div className="fs-logo-preview"><img src={logoPreview} alt={tr('sf3a3d92aee', "Uploaded establishment logo")} /><div><strong>{logo?.name}</strong><span>{tr('sf71224869d', "Ready to upload")}</span></div><button type="button" aria-label={tr('sb289ca0f79', "Remove logo")} onClick={() => setLogo(null)}><X size={17} /></button></div>}

              </div>

              <label className="fs-field"><span>{tr('sec468905f5', "Website or social page")} <small>{tr('sf53d1cd25e', "(optional)")}</small></span><input type="url" value={form.website_url} onChange={(e) => setField('website_url', e.target.value)} placeholder="https\://example.com" /><small>{tr('sb80028804a', "Use an existing business website or social media URL.")}</small></label>

            </div>}

            {step === 2 && <div className="fs-plan-grid">

              {[{ id: 'none', name: 'No subscription', price: 0, term: 'one-time', saving: tr('sfe6b005b71', "QR products only") }, { id: 'monthly', name: tr('sd30dbb5e3c', "Monthly plan"), price: 50, term: 'month' }, { id: 'yearly', name: tr('sd8da567768', "Annual plan"), price: 500, term: 'year', saving: tr('sd9570f160b', "Save 100 DH / year") }, { id: 'custom', name: tr('s53c3e8c8d2', "Custom offer"), price: null, term: 'negotiated', saving: tr('s491332bd26', "Set a price and duration") }].map((plan) => (

                <button key={plan.id} type="button" className={`fs-plan ${form.subscription_period === plan.id ? 'is-selected' : ''}`} onClick={() => setField('subscription_period', plan.id)} aria-pressed={form.subscription_period === plan.id}>

                  <span className="fs-plan-top"><span>{plan.name}</span><span className="fs-plan-check">{form.subscription_period === plan.id && <Check size={14} />}</span></span>

                  <span className="fs-plan-price">{plan.price === null ? tr('custom', 'Custom') : plan.price} <small>{plan.price === null ? tr('s3306ccd8b2', "price & duration") : `DH / ${plan.term}`}</small></span><span className="fs-plan-note">{plan.saving || tr('s1c5edd5926', "Flexible monthly subscription")}</span>

                </button>

              ))}

              {form.subscription_period === 'custom' && <div className="fs-fields fs-span-full" style={{ gridColumn: '1 / -1', paddingTop: 8 }}>
                <label className="fs-field"><span>{tr('s735f88bd15', "Custom subscription total price (DH)")}</span><input type="number" min="0" step="0.01" value={form.custom_subscription_price} onChange={(e) => setField('custom_subscription_price', e.target.value)} placeholder={tr('sf78c0077df', "e.g. 350")} /></label>
                <label className="fs-field"><span>{tr('se02d2ae03d', "Duration")}</span><input type="number" min="1" step="1" value={form.custom_subscription_length} onChange={(e) => setField('custom_subscription_length', e.target.value)} placeholder={tr('sc9df565134', "e.g. 6")} /></label>
                <div className="fs-field"><span>{tr('s8711c48964', "Duration unit")}</span><FieldDropdown label={tr('s8711c48964', "Duration unit")} value={form.custom_subscription_unit} options={[{value:'days',label:tr('se807d3ccf8', "Days")},{value:'weeks',label:tr('sbe71e9f41f', "Weeks")},{value:'months',label:tr('sef831a7b02', "Months")},{value:'years',label:tr('se4a00407f4', "Years")}]} onChange={(value) => setField('custom_subscription_unit', value)} /></div>
                <p className="fs-hint fs-span-full">{tr('sf650453610', "The custom price is for the entire selected duration, not per month.")}</p>
              </div>}
              <p className="fs-hint fs-span-full"><BadgeCheck size={17} />{tr('s88b7bfd011', "No subscription is allowed. Custom subscriptions require admin approval and backend support.")}</p>

            </div>}

            {step === 3 && <div className="fs-products-panel">

              <div className="fs-products-heading"><div><h3>{tr('s1ccc38a648', "QR print catalog")}</h3><p>{tr('sf1eaa3d6cf', "Choose products and quantities, or leave all quantities at zero.")}</p></div><span className="fs-counter">{productUnits} selected</span></div>

              <label className="fs-product-search"><Search size={18} /><input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder={tr('sc79a7a0037', "Search QR products")} aria-label={tr('sc79a7a0037', "Search QR products")} /></label>

              {catalogLoading ? <div className="fs-empty"><Loader2 className="fs-spin" size={27} /><p>{tr('se90e67338b', "Loading product catalog…")}</p></div> : catalogError ? <div className="fs-catalog-alert"><p>{catalogError}</p><button type="button" className="fs-secondary" onClick={loadProducts}><RefreshCw size={16} />{tr('s6327b4e59f', "Retry")}</button></div> : visibleProducts.length === 0 ? <div className="fs-empty"><Package size={27} /><p>{catalog.length === 0 ? tr('s998d444cbe', "No QR print products available.") : tr('s26298ed62f', "No matching products found.")}</p></div> : <div className="fs-products-list">{visibleProducts.map((product) => {

                const id = Number(product.id); const qty = quantities[id] || 0; const price = getPrice(product);

                return <div className="fs-product" key={product.id}><div className="fs-product-image"><FieldSalesProductImage productId={product.id} imagePath={product.main_image_path || product.main_image_url || product.image_url} alt={product.name || "QR product"}/></div><div className="fs-product-details"><strong>{product.name || product.title || `QR product #${id}`}</strong><span>{price === null ? tr('sa86c864c06', "Price on request") : `${formatMoney(price)} / unit`}</span>{qty > 0 && <span><strong>Line total: {price === null ? tr('sa86c864c06', "Price on request") : formatMoney(price * qty)}</strong> <small>({qty} × {price === null ? '—' : formatMoney(price)})</small></span>}<button type="button" className="fs-details-link" onClick={() => openProduct(product)}>{tr('s98a7344d1e', "View details & choose design")}</button>{productPreferences[id] && qty > 0 && <small>{tr('s25b22a00db', "Preferences saved")}</small>}</div><div className="fs-quantity"><button type="button" disabled={qty === 0} onClick={() => updateQuantity(id, qty - 1)} aria-label={`Decrease ${product.name || 'product'}`}><Minus size={16} /></button><input type="number" min="0" max="10000" value={qty} onChange={(e) => updateQuantity(id, e.target.value)} aria-label={`Quantity for ${product.name || 'product'}`} /><button type="button" disabled={qty >= 10000} onClick={() => updateQuantity(id, qty + 1)} aria-label={`Increase ${product.name || 'product'}`}><Plus size={16} /></button></div></div>;

              })}</div>}

              <label className="fs-field fs-notes"><span>{tr('s28c1870aad', "Visit notes")} <small>{tr('sf53d1cd25e', "(optional)")}</small></span><textarea rows={3} value={form.notes} onChange={(e) => setField('notes', e.target.value)} placeholder={tr('s50e092c7e3', "Customer preferences, requested follow-up, delivery notes…")} /></label>

              <div className="fs-review"><h3>{tr('sfd7b30a164', "Request summary")}</h3><div><span>{tr('sc1faa17a76', "Establishment")}</span><strong>{form.business_name || '—'}</strong></div><div><span>{tr('s787ad0b7a1', "Subscription")}</span><strong>{subscriptionLabel} — {formatMoney(subscriptionPrice)}</strong></div><div><span>{tr('s732ef16a5c', "QR units")}</span><strong>{productUnits}</strong></div><div><span>{tr('s5aacc90775', "QR products subtotal (before offer)")}</span><strong>{pricesComplete ? formatMoney(productTotal) : tr('sa86c864c06', "Price on request")}</strong></div><div><span>Buy 9, Get 1 Free ({pricing.freeUnits} free)</span><strong>{pricesComplete ? `−${formatMoney(pricing.discount)}` : tr('s41d67a85f3', "To be confirmed")}</strong></div><div><span>{tr('s79f42c44c2', "QR products after offer")}</span><strong>{pricesComplete ? formatMoney(discountedProductTotal) : tr('sa86c864c06', "Price on request")}</strong></div><div className="fs-review-total"><span>{tr('s0a72c91a19', "Estimated combined total (products + selected subscription)")}</span><strong>{pricesComplete ? formatMoney(subscriptionPrice + discountedProductTotal) : tr('sa86c864c06', "Price on request")}</strong></div><p>{tr('s3fb7c0ab30', "Promotion is a preview based on catalog prices and the cheapest eligible units. Final discount and pricing must be confirmed by the backend when the visit is processed.")}</p></div>

            </div>}

            {error && <div className="fs-error" role="alert">{error}</div>}

            <footer className="fs-form-footer"><button type="button" className="fs-secondary" disabled={step === 0 || saving} onClick={() => { setStep((current) => Math.max(0, current - 1)); setError(''); }}><ArrowLeft size={17} />{tr('s0557fa923d', "Back")}</button>{step < 3 ? <button type="button" className="fs-primary" onClick={() => goToStep(step + 1)}>{tr('sa0bfb8e59e', "Continue")} <ArrowRight size={17} /></button> : <button type="button" className="fs-primary" onClick={submitFinal} disabled={saving || step !== 3}>{saving ? <><Loader2 className="fs-spin" size={17} />{tr('sa96605ef72', "Submitting…")}</> : <>{tr('s56fc76b69a', "Submit for review")} <CheckCircle2 size={17} /></>}</button>}</footer>

          </form>

        </div>

        <aside className="fs-side-column"><div className="fs-side-card fs-surface"><span className="fs-side-icon"><ClipboardList size={21} /></span><h3>{tr('sfacb726741', "Visit overview")}</h3><p>{tr('sc5d2de79df', "Review your registration as you complete each step.")}</p><div className="fs-overview-row"><span>{tr('sd6e6cb19e3', "Business")}</span><strong>{form.business_name || tr('s0a62e62425', "Not entered yet")}</strong></div><div className="fs-overview-row"><span>{tr('sce5bf55137', "Location")}</span><strong>{form.city || tr('sdd3195ced9', "Not specified")}</strong></div><div className="fs-overview-row"><span>{tr('s787ad0b7a1', "Subscription")}</span><strong>{subscriptionLabel}</strong></div><div className="fs-overview-row"><span>{tr('s732ef16a5c', "QR units")}</span><strong>{productUnits}</strong></div><div className="fs-overview-row"><span>{tr('s83eed23c0e', "Products subtotal")}</span><strong>{pricesComplete ? formatMoney(productTotal) : tr('sa86c864c06', "Price on request")}</strong></div><div className="fs-overview-row"><span>{tr('s0fdc1992be', "Promotion savings")}</span><strong>{pricesComplete ? `−${formatMoney(pricing.discount)}` : tr('s41d67a85f3', "To be confirmed")}</strong></div><div className="fs-overview-row"><span>{tr('s05d00e9480', "Products after offer")}</span><strong>{pricesComplete ? formatMoney(discountedProductTotal) : tr('sa86c864c06', "Price on request")}</strong></div><div className="fs-overview-price"><span>{tr('secb83ebb23', "Subscription price")}</span><strong>{formatMoney(subscriptionPrice)}</strong><small>{form.subscription_period === 'none' ? tr('s4ccc09386c', "Not requested") : form.subscription_period === 'custom' ? tr('sd81ce5a6df', "Total for selected duration") : form.subscription_period === 'yearly' ? tr('s42cfc3e7ca', "per year") : tr('s8f2893f3cd', "per month")}</small></div></div><div className="fs-side-tip"><BadgeCheck size={19} /><p>{tr('sd473f75b04', "All new registrations are submitted for review. No subscription is activated automatically.")}</p></div></aside>

      </div>

      {activeProduct && (() => {

        const product = activeProduct;

        const id = Number(product.id);

        const pref = productPreferences[id] || {};

        const gallery = [...new Set([product.main_image_path, ...(Array.isArray(product.gallery) ? product.gallery : [])].filter(Boolean))];

        const select = (name, options) => <div className="fs-popup-field"><span>{name}</span><FieldDropdown label={`${name} for ${product.name}`} value={pref[name.toLowerCase()] || ''} options={options} onChange={(value) => setPreference(id, name.toLowerCase(), value)} /></div>;

        return <div className="fs-popup-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setActiveProduct(null); }}>

          <section className="fs-popup" role="dialog" aria-modal="true" aria-labelledby="fs-popup-title">

            <div className="fs-popup-header"><div><span className="fs-eyebrow">{tr('s3e3bb76f86', "QR PRODUCT DETAILS")}</span><h2 id="fs-popup-title">{product.name}</h2></div><button type="button" className="fs-popup-close" onClick={() => setActiveProduct(null)} aria-label={tr('s9ecac70fcd', "Close product details")}><X size={22} /></button></div>

            <div className="fs-popup-body">

              <div className="fs-popup-preview">

                <FieldSalesProductImage
                    productId={product.id}
                    imagePath={pref.design || product.main_image_path}
                    type={pref.design && pref.design !== product.main_image_path ? 'gallery' : 'main'}
                    index={pref.design && pref.design !== product.main_image_path ? (product.gallery || []).indexOf(pref.design) : 0}
                    alt={product.name}
                  />

                {gallery.length > 0 && <><p className="fs-popup-label">{tr('se157a03887', "Choose a design / reference image")}</p><div className="fs-popup-gallery">{gallery.map((path, index) => <button key={path} type="button" className={`fs-popup-thumbnail ${pref.design === path ? 'is-picked' : ''}`} onClick={() => setPreference(id, 'design', path)} aria-label={`Choose design ${index + 1}`} aria-pressed={pref.design === path}><FieldSalesProductImage productId={product.id} imagePath={path} type={path === product.main_image_path ? 'main' : 'gallery'} index={path === product.main_image_path ? 0 : (product.gallery || []).indexOf(path)} alt={`Design ${index + 1}`} /></button>)}</div></>}

              </div>

              <div className="fs-popup-config"><p className="fs-popup-description">{product.description || tr('s346b877dbe', "Choose the preferred print options for this product.")}</p><strong className="fs-popup-price">{getPrice(product) === null ? tr('sa86c864c06', "Price on request") : `${formatMoney(getPrice(product))} / unit`}</strong>

                <div className="fs-popup-options">{product.available_sizes?.length > 0 && select('Size', product.available_sizes)}{product.available_materials?.length > 0 && select('Material', product.available_materials)}

                  {product.available_colors?.length > 0 && select('Color', product.available_colors)}

                  <label className="fs-popup-field"><span>{tr('s9977562b9b', "Design request / customization notes")}</span><textarea rows={3} placeholder={tr('sfbd2f718da', "e.g. Logo centered, gold lettering, QR at the bottom…")} value={pref.design_notes || ''} onChange={(event) => setPreference(id, 'design_notes', event.target.value)} /></label>

                  <label className="fs-popup-field"><span>Quantity (minimum recommended: {product.minimum_quantity || 1})</span><input type="number" min="0" max="10000" value={quantities[id] || 0} onChange={(event) => updateQuantity(id, event.target.value)} /></label><div className="fs-popup-field"><span>{tr('s3932a81460', "Selected product total")}</span><strong>{getPrice(product) === null ? tr('sa86c864c06', "Price on request") : formatMoney(getPrice(product) * (quantities[id] || 0))}</strong></div>

                </div>

              </div>

            </div>

            <footer className="fs-popup-footer"><button type="button" className="fs-primary" onClick={() => setActiveProduct(null)}><Check size={17} />{tr('sb089ea5476', "Save preferences")}</button></footer>

          </section>

        </div>;

      })()}

    </main>

  );

}
